// eslint-disable-next-line @typescript-eslint/no-var-requires
const { loggableUrl } = require('./loggable-url') as { loggableUrl: (url: string) => string };

/** The parts of a Sentry event that carry a URL. */
export interface UrlCarryingEvent {
  request?: { url?: string; query_string?: unknown; headers?: Record<string, string> };
  breadcrumbs?: Array<{ data?: Record<string, unknown> }>;
}

/**
 * Some of orion-web's own URLs carry a link's credential — a share card's token in its path, a pack
 * invite inside `deepLink` — and Sentry copies the page address into every event and every navigation
 * and request breadcrumb. Each of those URLs is rewritten the way our log lines write them, keeping its
 * origin and path shape so an event still says which page it came from.
 */
export function redactTokenUrls<E extends UrlCarryingEvent>(event: E): E {
  if (event.request) {
    if (typeof event.request.url === 'string') {
      event.request.url = redactUrl(event.request.url);
    }
    delete event.request.query_string;
    // A Referer, or the forwarded URL the edge adds, can name the same token-bearing page.
    for (const [name, value] of Object.entries(event.request.headers ?? {})) {
      if (typeof value === 'string' && /^(https?:\/\/|\/)/i.test(value)) {
        event.request.headers![name] = redactUrl(value);
      }
    }
  }
  for (const breadcrumb of event.breadcrumbs ?? []) {
    for (const key of ['url', 'from', 'to']) {
      const value = breadcrumb.data?.[key];
      if (typeof value === 'string') {
        breadcrumb.data![key] = redactUrl(value);
      }
    }
  }
  return event;
}

function redactUrl(url: string): string {
  const match = /^([a-z][a-z0-9+.-]*:\/\/[^/?#]*)?(.*)$/i.exec(url);
  const origin = match?.[1] ?? '';
  const rest = match?.[2] ?? url;
  const pathAndQuery = rest.split('#')[0];
  return `${origin}${loggableUrl(pathAndQuery.startsWith('/') || !pathAndQuery ? pathAndQuery : `/${pathAndQuery}`)}`;
}
